import { useEffect, useState } from "react";
import type { QuizListItem, ResultDetail, ResultSummary } from "../../../shared/types";
import { CoffeeCup } from "../../components/motifs";
import { Ico } from "../../components/shell";
import { AtlShell, QuizMark, StatusChip, ATL_ANS } from "../../components/atelier";
import { navigate } from "../../router";
import { api } from "../../api";
import { useAuth } from "../../auth";
import LaunchModal from "./LaunchModal";
import QuizPickerModal from "./QuizPickerModal";

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

export default function Dashboard() {
  const { user } = useAuth();
  const [quizzes, setQuizzes] = useState<QuizListItem[] | null>(null);
  const [results, setResults] = useState<ResultSummary[]>([]);
  const [latest, setLatest] = useState<ResultDetail | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [bankCount, setBankCount] = useState<number | null>(null);
  const [launch, setLaunch] = useState<{ id: string; title: string } | null>(null);
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    api.listQuizzes().then((r) => setQuizzes(r.quizzes)).catch(() => setQuizzes([]));
    api.listBank().then((r) => setBankCount(r.questions.length)).catch(() => setBankCount(0));
    api
      .listResults()
      .then((r) => {
        setResults(r.results);
        if (r.results[0]) api.getResult(r.results[0].id).then((d) => setLatest(d.result)).catch(() => {});
      })
      .catch(() => {});
  }, []);

  /** Open the pre-launch config popup for a quiz (it owns the launch). */
  function host(quizId: string) {
    const q = quizzes?.find((x) => x.id === quizId);
    setLaunch({ id: quizId, title: q?.title ?? "Live quiz" });
  }

  /** "Start live quiz": let the host choose which quiz — never silently host a
      random one. Skips the picker only when there's a single (or no) quiz. */
  function startLive() {
    if (!quizzes) return; // still loading
    if (quizzes.length === 0) return navigate("/builder");
    if (quizzes.length === 1) return host(quizzes[0].id);
    setPicking(true);
  }

  const firstName = (user?.displayName ?? "Baker").split(/\s+/)[0];
  const totalQuestions = (quizzes ?? []).reduce((n, q) => n + q.questionCount, 0);
  const avgScore = results.length
    ? Math.round(results.reduce((n, r) => n + r.avgCorrectPct, 0) / results.length)
    : null;
  const quizCount = quizzes?.length ?? 0;

  const actions = [
    { ic: "build", t: "Create quiz", s: "Start a fresh bake", onClick: () => navigate("/builder") },
    { ic: "play", t: "Start live", s: "Open the oven doors", onClick: startLive },
    { ic: "chart", t: "View results", s: "See what rose", onClick: () => navigate("/results") },
    {
      ic: "bank",
      t: "Question bank",
      s: bankCount == null ? "Loading…" : `${bankCount} ready`,
      onClick: () => navigate("/bank"),
    },
  ] as const;

  return (
    <AtlShell
      active="Dashboard"
      title="Dashboard"
      sub={new Date()
        .toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })
        .toUpperCase()}
    >
      <div style={{ padding: 30, display: "flex", flexDirection: "column", gap: 18, overflow: "hidden" }}>
        {/* hero */}
        <div className="atl-card atl-hero" style={{ padding: "28px 32px", display: "flex", alignItems: "center", gap: 30 }}>
          <div style={{ flex: 1 }}>
            <div className="atl-eyebrow atl-eyebrow--accent">
              ● {quizCount} QUIZ{quizCount === 1 ? "" : "ZES"} IN YOUR KITCHEN
            </div>
            <h2 className="atl-serif" style={{ fontSize: 36, marginTop: 12 }}>
              {greeting()}, <span style={{ fontStyle: "italic" }}>{firstName}.</span>
            </h2>
            <p style={{ color: "var(--atl-soft)", fontSize: 15, fontWeight: 500, marginTop: 8 }}>
              The ovens are warm — your team is ready to rise.
            </p>
            <div className="atl-herostats" style={{ display: "flex", gap: 0, marginTop: 22 }}>
              {[
                [totalQuestions.toLocaleString(), "Questions baked"],
                [results.length.toString(), "Sessions hosted"],
                [avgScore == null ? "—" : `${avgScore}%`, "Avg. correct-rate"],
              ].map(([n, l], i) => (
                <div
                  key={l}
                  className="atl-herostat"
                  style={{ paddingRight: 28, marginRight: 28, borderRight: i < 2 ? "1px solid var(--atl-line)" : "none" }}
                >
                  <div className="atl-serif" style={{ fontSize: 30, color: "var(--atl-accent)" }}>
                    {n}
                  </div>
                  <div className="atl-eyebrow" style={{ marginTop: 5 }}>
                    {l}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div
            className="atl-hero-aside"
            style={{
              width: 200,
              flex: "0 0 200px",
              borderLeft: "1px solid var(--atl-line)",
              paddingLeft: 28,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 16,
            }}
          >
            <span
              className="atl-hero-cup"
              style={{
                width: 86,
                height: 86,
                borderRadius: 24,
                background: "var(--atl-accent)",
                display: "grid",
                placeItems: "center",
                boxShadow: "0 10px 26px rgba(140,47,61,.26)",
              }}
            >
              <CoffeeCup size={44} color="#fff" />
            </span>
            <button
              className="atl-btn atl-btn--primary atl-btn--block"
              onClick={startLive}
              disabled={!!busyId}
            >
              <Ico name="play" size={15} /> {busyId ? "Lighting the oven…" : "Start live quiz"}
            </button>
          </div>
        </div>

        {/* quick actions */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 14 }}>
          {actions.map((a) => (
            <div
              key={a.t}
              className="atl-card"
              onClick={a.onClick}
              style={{ padding: 20, display: "flex", flexDirection: "column", gap: 22, cursor: "pointer" }}
            >
              <span
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: "50%",
                  border: "1px solid var(--atl-line)",
                  display: "grid",
                  placeItems: "center",
                  color: "var(--atl-accent)",
                }}
              >
                <Ico name={a.ic} size={19} />
              </span>
              <div>
                <div className="atl-serif" style={{ fontSize: 18 }}>
                  {a.t}
                </div>
                <div style={{ fontSize: 12.5, fontWeight: 500, color: "var(--atl-faint)", marginTop: 2 }}>{a.s}</div>
              </div>
            </div>
          ))}
        </div>

        {/* split */}
        <div className="atl-respond" style={{ display: "grid", gridTemplateColumns: "1.55fr 1fr", gap: 16 }}>
          <div className="atl-card" style={{ padding: 24 }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
              <h3 className="atl-serif" style={{ fontSize: 19 }}>
                Your quizzes
              </h3>
              <button
                className="atl-btn atl-btn--link"
                style={{ marginLeft: "auto", fontSize: 13 }}
                onClick={() => navigate("/quizzes")}
              >
                View all
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {quizzes === null && (
                <div style={{ color: "var(--atl-faint)", fontWeight: 500, padding: "8px 0" }}>Loading your bakes…</div>
              )}
              {quizzes?.length === 0 && (
                <div style={{ color: "var(--atl-soft)", fontWeight: 500, padding: "8px 0" }}>
                  No quizzes yet —{" "}
                  <span className="atl-link2" onClick={() => navigate("/builder")}>
                    bake your first one
                  </span>
                  .
                </div>
              )}
              {quizzes?.map((r, i) => (
                <div key={r.id}>
                  <div style={{ display: "flex", alignItems: "center", gap: 15, padding: "11px 0", flexWrap: "wrap", rowGap: 10 }}>
                    <span
                      style={{ cursor: "pointer", flex: "0 0 auto" }}
                      onClick={() => navigate(`/builder/${r.id}`)}
                    >
                      <QuizMark c={ATL_ANS[i % ATL_ANS.length]} size={42} emoji={r.coverEmoji} />
                    </span>
                    <div
                      style={{ flex: "1 1 140px", minWidth: 0, cursor: "pointer" }}
                      onClick={() => navigate(`/builder/${r.id}`)}
                    >
                      <div className="atl-serif" style={{ fontSize: 16.5 }}>
                        {r.title}
                      </div>
                      <div style={{ display: "flex", gap: 9, marginTop: 4, alignItems: "center", flexWrap: "wrap", rowGap: 4 }}>
                        <StatusChip status={r.status} />
                        <span style={{ fontSize: 12.5, color: "var(--atl-faint)", fontWeight: 500 }}>
                          {r.questionCount} question{r.questionCount === 1 ? "" : "s"} · {r.scoring} scoring
                        </span>
                      </div>
                    </div>
                    <button
                      className="atl-btn atl-btn--ghost"
                      style={{ padding: "8px 14px", fontSize: 13 }}
                      onClick={() => navigate(`/builder/${r.id}`)}
                    >
                      Edit
                    </button>
                    <button
                      className="atl-btn atl-btn--primary"
                      style={{ padding: "8px 15px", fontSize: 13 }}
                      disabled={r.questionCount === 0 || !!busyId}
                      onClick={() => host(r.id)}
                    >
                      <Ico name="play" size={13} /> Host
                    </button>
                  </div>
                  {i < quizzes.length - 1 && <hr className="atl-rule" style={{ background: "var(--atl-line-2)" }} />}
                </div>
              ))}
            </div>
          </div>

          <div className="atl-card" style={{ padding: 24, background: "var(--atl-surface-2)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 16 }}>
              <span style={{ color: "var(--atl-gold)" }}>
                <Ico name="trophy" size={18} />
              </span>
              <h3 className="atl-serif" style={{ fontSize: 19 }}>
                {latest ? "Last session's podium" : "Top scorers"}
              </h3>
            </div>
            {latest && latest.standings.length > 0 ? (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {latest.standings.slice(0, 4).map((t, i) => (
                    <div key={t.nickname} style={{ display: "flex", alignItems: "center", gap: 13 }}>
                      <span
                        className="atl-serif"
                        style={{ width: 18, fontSize: 16, color: i === 0 ? "var(--atl-gold)" : "var(--atl-faint)" }}
                      >
                        {i + 1}
                      </span>
                      <span
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: 9,
                          background: t.color ?? ATL_ANS[i % ATL_ANS.length],
                          color: "#fff",
                          display: "grid",
                          placeItems: "center",
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        {t.init ?? t.nickname.slice(0, 2).toUpperCase()}
                      </span>
                      <span
                        style={{
                          fontWeight: 500,
                          fontSize: 14,
                          flex: 1,
                          minWidth: 0,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {t.nickname}
                      </span>
                      <span className="atl-mono" style={{ fontWeight: 700, fontSize: 13.5 }}>
                        {t.score.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
                <hr className="atl-rule" style={{ margin: "18px 0 14px", background: "var(--atl-line-2)" }} />
                <p style={{ fontSize: 13, color: "var(--atl-soft)", fontWeight: 500, lineHeight: 1.5 }}>
                  <span className="atl-serif" style={{ fontStyle: "italic", color: "var(--atl-ink)" }}>
                    {latest.quizTitle} —
                  </span>{" "}
                  {latest.avgCorrectPct}% correct on average.{" "}
                  <span className="atl-link2" onClick={() => navigate(`/results/${latest.id}`)}>
                    See insights →
                  </span>
                </p>
              </>
            ) : (
              <p style={{ fontSize: 13, color: "var(--atl-soft)", fontWeight: 500, lineHeight: 1.5 }}>
                <span className="atl-serif" style={{ fontStyle: "italic", color: "var(--atl-ink)" }}>
                  No sessions yet.
                </span>{" "}
                Host a quiz and your podium + per-question insights will land here.
              </p>
            )}
          </div>
        </div>
      </div>
      {picking && (
        <QuizPickerModal
          quizzes={quizzes ?? []}
          onPick={(id) => {
            setPicking(false);
            host(id);
          }}
          onClose={() => setPicking(false)}
        />
      )}
      {launch && (
        <LaunchModal quizId={launch.id} quizTitle={launch.title} onClose={() => setLaunch(null)} />
      )}
    </AtlShell>
  );
}
